import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useTenant } from '@core/tenant/TenantProvider';
import { TenantGuard } from '@core/tenant/TenantGuard';
import { AnalyticsProvider, RouteTracker } from '@core/analytics';
import { BookingScopeSetter } from '@core/sentry';
import { BookingProvider } from '@features/booking';

// Feature imports
import { IndexPage } from '@features/landing';
import {
  SelectDinersPage,
  SelectDatePage,
  FullCalendarPage,
  SelectServicePage,
  EarlyAccessPage,
  SelectTimePage,
  SelectExperiencePage,
  SelectAreaPage,
  PersonalizePage,
  ConfirmationPage,
  BookingFinalPage,
  BookingPaymentPage,
  SelectMenuFilterPage,
  SelectAreaFilterPage,
  MaridajePage,
  CompleteProfilePage,
  WaitingListFormPage,
  WaitingListCalendarPage,
  WaitingListAlternativesPage,
  DeepLinkRedirectPage,
  MeitreNetworkRedirectPage,
  StaffBookingRedirectPage,
  AgentGuestInfoPage,
  ConfirmEarlyAccessPage,
  ConfirmGuaranteePage,
  ConfirmReservationPage,
  ReservationStatusPage,
  AddToCalendarPage,
  UnsubscribePage,
  TermsAndConditionsPage,
} from '@features/booking';
import { SignUpPage, SignInPage, ConfirmEmailPage } from '@features/auth';
import { AuthReturnPage } from '@features/auth/pages/AuthReturnPage';
import { AuthReturnGate } from '@features/auth/pages/AuthReturnGate';
import AuthMagicLinkPage from '@features/auth/pages/AuthMagicLinkPage';
import { ResetPasswordPage } from '@features/auth/pages/ResetPasswordPage';
import { GiftCardPurchasePage, RedeemGiftCardPage } from '@features/gift-cards';
import { ProfilePage } from '@features/profile';
import { ReviewPage } from '@features/review';
import { NotFoundPage } from '@shared/components/common';
import GuaranteeAdjustmentPage from '@features/booking/pages/GuaranteeAdjustmentPage';

// DEV-only: lazy-loaded verification harness. In prod, `import.meta.env.DEV` is
// `false`, so `VerifyApp` is `null`, the route below is never rendered, and the
// `lazy(() => import(...))` call is dead code — Rollup never emits the chunk,
// so the entire verify subtree (incl. @testing-library) is excluded from
// `dist/`.
const VerifyApp = import.meta.env.DEV
  ? lazy(() => import('@shared/verify/dev-app'))
  : null;

/**
 * Layout that guards content behind TenantGuard and provides
 * AnalyticsProvider + BookingProvider. All routes that need
 * restaurant data use this layout.
 */
function TenantLayout() {
  const { restaurant } = useTenant();

  return (
    <TenantGuard>
      <AnalyticsProvider restaurant={restaurant!}>
        <RouteTracker />
        <BookingProvider>
          <BookingScopeSetter />
          <Outlet />
        </BookingProvider>
      </AnalyticsProvider>
    </TenantGuard>
  );
}

/**
 * TenantLayout without analytics, for a page whose path carries a bearer token:
 * page views, tags and pixels would send it to third parties.
 */
function UntrackedTenantLayout() {
  return (
    <TenantGuard>
      <BookingProvider>
        <BookingScopeSetter />
        <Outlet />
      </BookingProvider>
    </TenantGuard>
  );
}

/**
 * Application routes configuration.
 *
 * /auth-return lives OUTSIDE TenantLayout so it works even when
 * the restaurant API times out. All other routes are inside
 * TenantLayout which provides TenantGuard + Analytics + Booking.
 */
export function AppRouter() {
  return (
    <Routes>
      {/* Auth callback — outside TenantGuard, only needs AuthProvider */}
      <Route path="/auth-return" element={<AuthReturnGate />} />
      <Route path="/auth/:token" element={<AuthMagicLinkPage />} />

      {/* DEV-only verification harness — lazy-loaded so prod bundles exclude it. */}
      {import.meta.env.DEV && VerifyApp && (
        <Route
          path="/verify/*"
          element={
            <Suspense fallback={<div style={{ padding: 24 }}>loading verify…</div>}>
              <VerifyApp />
            </Suspense>
          }
        />
      )}

      {/* Standalone: pay a deposit difference staff opened (email link) */}
      <Route element={<UntrackedTenantLayout />}>
        <Route path="/guarantee-adjustment/:token" element={<GuaranteeAdjustmentPage />} />
      </Route>

      {/* All other routes — guarded by TenantLayout */}
      <Route element={<TenantLayout />}>
        {/* Home */}
        <Route path="/" element={<IndexPage />} />

        {/* Booking Flow */}
        <Route path="/select-diners" element={<SelectDinersPage />} />
        <Route path="/select-date" element={<SelectDatePage />} />
        <Route path="/full-calendar" element={<FullCalendarPage />} />
        <Route path="/select-service" element={<SelectServicePage />} />
        <Route path="/early-access" element={<EarlyAccessPage />} />
        <Route path="/select-menu-filter" element={<SelectMenuFilterPage />} />
        <Route path="/select-area-filter" element={<SelectAreaFilterPage />} />
        <Route path="/select-experience" element={<SelectExperiencePage />} />
        <Route path="/select-area" element={<SelectAreaPage />} />
        <Route path="/select-time" element={<SelectTimePage />} />

        {/* Auth (in-flow) */}
        <Route path="/sign-up" element={<SignUpPage />} />
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/auth-return-complete" element={<AuthReturnPage />} />
        <Route path="/reset/:token" element={<ResetPasswordPage />} />
        <Route path="/confirm-email" element={<ConfirmEmailPage />} />
        <Route path="/complete-profile" element={<CompleteProfilePage />} />

        {/* Waiting List */}
        <Route path="/waiting-list" element={<WaitingListFormPage />} />
        <Route path="/waiting-list-calendar" element={<WaitingListCalendarPage />} />
        <Route path="/waiting-list-alternatives" element={<WaitingListAlternativesPage />} />

        {/* Pre-booking: pairings */}
        <Route path="/maridaje" element={<MaridajePage />} />

        {/* Post-booking */}
        <Route path="/personalize" element={<PersonalizePage />} />
        <Route path="/agent-guest-info" element={<AgentGuestInfoPage />} />
        <Route path="/payment" element={<BookingPaymentPage />} />
        <Route path="/confirmation" element={<ConfirmationPage />} />
        <Route path="/booking-final" element={<BookingFinalPage />} />

        {/* Standalone: confirm early access */}
        <Route path="/confirm-early-access" element={<ConfirmEarlyAccessPage />} />

        {/* Standalone: confirm guarantee payment */}
        <Route path="/confirm-guarantee" element={<ConfirmGuaranteePage />} />

        {/* Standalone: confirm reservation from email link */}
        <Route path="/confirm-reservation/:id" element={<ConfirmReservationPage />} />

        {/* Standalone: reservation status (SMS/email link) */}
        <Route path="/reservation-status/:token" element={<ReservationStatusPage />} />

        {/* Standalone: add the reservation to the guest's calendar (email link).
            The backend builds this URL in NotificationService::emailURLsBuilder
            as `{frontendBaseUrl}add-to-calendar/{encryptedId}` — the path is a
            contract with every reservation email already in inboxes. */}
        <Route path="/add-to-calendar/:id" element={<AddToCalendarPage />} />

        {/* Standalone: post-visit review */}
        <Route path="/review/:resId/:lang" element={<ReviewPage />} />

        {/* Standalone: email unsubscribe confirmation */}
        <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />

        {/* Standalone: terms and conditions (linked from emails) */}
        <Route path="/terms-and-conditions/:lang" element={<TermsAndConditionsPage />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />

        {/* Gift Cards */}
        <Route path="/gift-card" element={<GiftCardPurchasePage />} />
        <Route path="/redeem-gift-card/:token" element={<RedeemGiftCardPage />} />

        {/* Profile */}
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/user/account" element={<Navigate to="/profile" replace />} />

        {/* Deep-links — partial forms land deeper in the flow as more params are supplied */}
        <Route path="/search/:date" element={<DeepLinkRedirectPage />} />
        <Route path="/search/:date/:partySize" element={<DeepLinkRedirectPage />} />
        <Route path="/search/:date/:partySize/:serviceType" element={<DeepLinkRedirectPage />} />
        <Route path="/search/:date/:partySize/:serviceType/:centerHour" element={<DeepLinkRedirectPage />} />
        <Route path="/waiting-list-alternative/:date/:partySize/:serviceType/:centerHour/:wlResId" element={<DeepLinkRedirectPage />} />
        <Route path="/reserva" element={<MeitreNetworkRedirectPage />} />
        <Route path="/book" element={<MeitreNetworkRedirectPage />} />
        <Route path="/staff" element={<StaffBookingRedirectPage />} />
        <Route path="/platform" element={<StaffBookingRedirectPage />} />

        {/* Catch-all - must be last */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

export default AppRouter;
